import type { MobileDatePickerMode } from '@byecode/ui'
import { IconFont, MobileDatePicker, Text } from '@byecode/ui'
import type { DateConfig, DateField, DateValue, FieldInputADTValue, FieldProtocol } from '@lighthouse/core'
import { add, endOfDay, format, isValid, lightFormat, parse, parseISO, startOfDay } from 'date-fns'
import zhCN from 'date-fns/locale/zh-CN/index'
import { max } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { useApplicationContext } from '../../../../../contexts'
import { extractLunarDate, format2LunarDate } from '../../../../../utils'
import { useAppThemeContext } from '../../../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../../../FillPicker'
import { defaultFormat } from '../../../constant'
import { SelectPlaceHolder } from '../../../SelectPlaceHolder'
import type { FieldBaseProps } from '../../../types'
import * as CM from '../../commonStyles'
import { getRelativeDate, getTimestamp } from '../help'

interface DateDrawerFieldProps extends FieldBaseProps {
    value: DateValue
    dateConfig: DateConfig
    dateField?: DateField | FieldProtocol
}

const SCxContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    min-height: 38px;
    font-size: var(--font-size-normal);
    border-radius: inherit;

    &[data-field-disabled='true'] {
        cursor: not-allowed;
    }
`

export const DateDrawerField: React.FunctionComponent<DateDrawerFieldProps> = props => {
    const { onChange, value = '', isControlled, dateConfig, dateField, readOnly, language } = props

    const fieldType = dateField?.type || 'date'
    const { theme } = useAppThemeContext()
    const { placeholder, date, breakPoint } = dateConfig ?? {}
    const { placeholderColor, fontFamily } = useMemo(
        () => ({
            placeholderColor: applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes),
            fontFamily: breakPoint?.font?.fontFamily
        }),
        [breakPoint?.design?.placeholderColor?.color, breakPoint?.font?.fontFamily, theme.palettes]
    )
    const { dateFormat, range = 'ALL', customDays } = date ?? {}

    const withLunarFormat = useMemo(() => {
        if (dateField?.type === 'date') {
            return (dateField as DateField).date.format ?? defaultFormat
        }

        if (dateField?.type === 'text') {
            return dateFormat ?? 'yyyy/MM/dd'
        }

        return defaultFormat
    }, [dateField, dateFormat])

    const [isLunar, finalFormat] = useMemo(() => {
        const includesLunar = withLunarFormat.includes('lunar-')
        const normalFormat = withLunarFormat
            .split(' ')
            .map(item => item.replace('lunar-', ''))
            .join(' ')
        return [includesLunar, normalFormat]
    }, [withLunarFormat])

    const timestamp = useMemo(() => {
        if (fieldType === 'text') {
            if (!value) {
                return value
            }

            // 农历日期格式
            if (isLunar) {
                return extractLunarDate(value.toString())?.getTime() || ''
            }

            const date = parse(value.toString(), finalFormat, new Date())
            if (isValid(date)) {
                return date.getTime()
            }

            return ''
        }

        return getTimestamp(value)
    }, [fieldType, finalFormat, isLunar, value])
    const { pageTarget } = useApplicationContext()

    const [data, setData] = useState(timestamp)

    const usedValue = useMemo(() => {
        if (isControlled) {
            if (!value) {
                return value
            }
            if (fieldType === 'text') {
                // 农历日期格式
                if (isLunar) {
                    return extractLunarDate(value.toString())?.getTime() || ''
                }

                const date = parse(value.toString(), finalFormat, new Date())
                if (isValid(date)) {
                    return date.getTime()
                }

                return ''
            }
            return value
        }
        return data
    }, [data, fieldType, finalFormat, isControlled, isLunar, value])

    const isEmpty = usedValue === ''

    const suffixText = useMemo(() => {
        const currentTimestamp = parseISO(format(Date.now(), 'yyyy-MM-dd')).valueOf()
        const previewTimeStamp = usedValue ? Number(usedValue) : ''
        return previewTimeStamp && !isLunar ? getRelativeDate(previewTimeStamp, currentTimestamp, language) : ''
    }, [isLunar, language, usedValue])

    const [minDate, maxDate] = useMemo(() => {
        switch (range) {
            case 'AFTER_TODAY': {
                return [startOfDay(new Date()), undefined]
            }
            case 'BEFORE_TODAY': {
                return [undefined, endOfDay(new Date())]
            }
            case 'AFTER_CUSTOM': {
                return [
                    customDays
                        ? add(startOfDay(new Date()), {
                              days: max(customDays, 0)
                          })
                        : undefined,
                    undefined
                ]
            }
            default: {
                return []
            }
        }
    }, [customDays, range])

    const handleChange = useCallback(
        (updateData?: Date) => {
            setData(updateData ? updateData.valueOf() : '')
            if (fieldType === 'text') {
                if (!updateData) {
                    onChange?.({ type: 'text', value: '' })
                    return
                }

                if (isLunar) {
                    onChange?.({
                        type: 'text',
                        value: format2LunarDate(updateData, withLunarFormat)
                    })

                    return
                }
                onChange?.({ type: 'text', value: lightFormat(updateData, finalFormat) })
            } else {
                onChange?.({ type: 'date', value: updateData ? updateData.valueOf() : '' })
            }
        },
        [fieldType, finalFormat, isLunar, onChange, withLunarFormat]
    )

    const mode = useMemo<MobileDatePickerMode>(() => {
        if (finalFormat === 'HH:mm') {
            return 'time'
        }

        if (finalFormat === 'yyyy/MM' || finalFormat === 'yyyy-MM') {
            return 'month'
        }

        if (finalFormat.includes('HH')) {
            return 'full'
        }

        return 'day'
    }, [finalFormat])

    return (
        <>
            <MobileDatePicker
                target={pageTarget}
                mode={mode}
                placeholder="请选择"
                disabled={readOnly}
                style={{ fontFamily }}
                customRenderInputUi={(_, open, onTrigger) => {
                    return (
                        <SCxContainer
                            data-field-input
                            data-field-active={open}
                            data-field-disabled={!!readOnly}
                            onClick={() => !readOnly && onTrigger()}
                        >
                            {isEmpty ? (
                                <SelectPlaceHolder color={placeholderColor} label={placeholder} />
                            ) : (
                                <Text>
                                    {isLunar
                                        ? format2LunarDate(new Date(usedValue), withLunarFormat)
                                        : format(Number(usedValue), finalFormat, {
                                              locale: zhCN
                                          })}
                                    {suffixText}
                                </Text>
                            )}
                            <CM.IconWrapper>
                                {!readOnly && <IconFont type="ArrowDownSmall" color="var(--color-gray-400)" size={16} />}
                            </CM.IconWrapper>
                        </SCxContainer>
                    )
                }}
                // style={{ opacity: 0, position: 'absolute', inset: 0 }}
                maxDate={maxDate}
                minDate={minDate}
                modalProps={{
                    style: { fontFamily },
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    'data-ignore-click-away': true
                }}
                onChange={handleChange}
            />
        </>
    )
}
